import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export function ThemeProvider({ children }) {
  // Get initial theme from localStorage or default to 'system'
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved || 'system';
  });

  // Get the actual applied theme (resolves 'system' to 'light' or 'dark')
  const [appliedTheme, setAppliedTheme] = useState('light');

  // Update applied theme based on theme setting and system preference
  useEffect(() => {
    const updateAppliedTheme = () => {
      if (theme === 'system') {
        const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setAppliedTheme(systemDark ? 'dark' : 'light');
      } else {
        setAppliedTheme(theme);
      }
    };

    updateAppliedTheme();

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        updateAppliedTheme();
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Apply theme class to document
  useEffect(() => {
    const root = document.documentElement;
    
    if (appliedTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [appliedTheme]);

  // Save theme to localStorage when changed
  const changeTheme = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, appliedTheme, setTheme: changeTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export default ThemeContext;
